import React, { useState, useEffect, useCallback } from 'react';
import api, { getSocket } from '../../api';
import LeagueMetrics from '../LeagueMetrics/LeagueMetrics';
import LockOverview from '../LockOverview/LockOverview';
import MobileLeagueStatsShell from '../mobile/MobileLeagueStatsShell';
import { useSeason } from '../../context/SeasonContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useMobile } from '../../context/MobileContext';
import HiddenText from '../HiddenText';
import LeaderboardTableSkeleton from '../loading/LeaderboardTableSkeleton';
import LivePulseValue from '../LivePulseValue';
import '../LivePulse.css';
import './Leaderboard.css';

const getWeekDisplay = (week) => {
  return `BW${week.bowlWeekNumber}P${week.pickNumber}`;
};

const getGamesBehindDisplay = (gamesBehind) => {
  if (gamesBehind === undefined || gamesBehind === null) return '-';
  return gamesBehind === 0 ? '-' : gamesBehind.toFixed(1);
};

const getMarginDisplay = (margin) => {
  return margin !== undefined ? margin.toFixed(1) : '0.0';
};

const getWeekStatus = (status) => {
  switch (status?.toLowerCase()) {
    case 'won':
    case 'w':
      return 'W';
    case 'lost':
    case 'l':
      return 'L';
    case 'bye':
    case 'b':
      return 'B';
    case 'pending':
    case 'pm':
      return 'PM';
    case 'winning':
      return "W'ing";
    case 'losing':
      return "L'ing";
    default:
      return '-';
  }
};

const isLiveWeekStatus = (status) => status === 'winning' || status === 'losing';

const getBestPossibleDisplay = (bestPossible, currentLosses, effectiveLosses) => {
  if (bestPossible === undefined || bestPossible === null) return '-';
  // Use effective losses for the display to account for unused byes
  const displayLosses = effectiveLosses !== undefined ? effectiveLosses : currentLosses;
  return `${bestPossible}-${displayLosses}`;
};

const getLocksRemainingDisplay = (locksRemaining, byeUsed) => {
  if (locksRemaining === undefined || locksRemaining === null) return '-';
  return locksRemaining;  // Will now display either "3+Bye" or just "3" depending on bye status
};

const LeagueStats = () => {
  const [activeView, setActiveView] = useState('leaderboard');
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [allActiveUsers, setAllActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const { effectiveSeason } = useSeason();
  const { settings } = useLeagueSettings();
  const { user, locksCache, fetchLocksForWeek, getMaxPicksForWeek } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { isMobile } = useMobile();

  // Check if user has reached max picks for the current week
  const hasReachedMaxPicks = () => {
    if (!currentWeek || !locksCache[currentWeek]) return false;

    const locksForWeek = locksCache[currentWeek];
    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    // Get the actual max picks for this week from the backend
    const maxPicksForWeek = getMaxPicksForWeek(currentWeek);

    return userLocksCount >= maxPicksForWeek;
  };



  const renderViewToggle = () => {
    const isArchivedSeason = selectedSeason && selectedSeason < effectiveSeason;

    return (
      <div className="view-toggle">
        <div className="season-selector">
          <label htmlFor="season-select">Season:</label>
          <select
            id="season-select"
            value={selectedSeason || effectiveSeason || ''}
            onChange={(e) => setSelectedSeason(parseInt(e.target.value))}
            className={isArchivedSeason ? 'archived-season' : ''}
          >
                         {Array.isArray(availableSeasons) && availableSeasons.length > 0 ? (
               availableSeasons.map(season => (
                 <option key={season} value={season}>
                   {season === effectiveSeason ? `Current (${season})` : season}
                 </option>
               ))
             ) : (
               <option value={effectiveSeason}>Current ({effectiveSeason})</option>
             )}
          </select>
        </div>
        <div className="tab-buttons">
          <button
            className={`toggle-button ${activeView === 'leaderboard' ? 'active' : ''}`}
            onClick={() => setActiveView('leaderboard')}
          >
            Leaderboard
          </button>
          <button
            className={`toggle-button ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveView('overview')}
          >
            League Overview
          </button>
          <button
            className={`toggle-button ${activeView === 'metrics' ? 'active' : ''}`}
            onClick={() => setActiveView('metrics')}
          >
            League Metrics
          </button>
          <button
            className={`toggle-button ${activeView === 'lock-overview' ? 'active' : ''}`}
            onClick={() => setActiveView('lock-overview')}
          >
            Lock Overview
          </button>
        </div>
      </div>
    );
  };

  // Initialize selected season to current season
  useEffect(() => {
    if (!selectedSeason) {
      setSelectedSeason(effectiveSeason);
    }
  }, [effectiveSeason, selectedSeason]);

  // Fetch available seasons
  useEffect(() => {
    const fetchAvailableSeasons = async () => {
      try {
        const response = await api.getAvailableSeasons();
        // The API returns { seasons: [...] }, so we need to access response.data.seasons
        const seasons = Array.isArray(response.data?.seasons) ? response.data.seasons : [];
        setAvailableSeasons(seasons);
      } catch (err) {
        console.error('Error fetching available seasons:', err);
        // Set empty array on error to prevent crashes
        setAvailableSeasons([]);
      }
    };

    fetchAvailableSeasons();
  }, []);

  // Fetch all active users
  useEffect(() => {
    const fetchAllActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAllActiveUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching active users:', err);
        setAllActiveUsers([]);
      }
    };

    fetchAllActiveUsers();
  }, []);

  // Fetch locks for all weeks when League Stats loads
  useEffect(() => {
    const fetchAllLocks = async () => {
      if (!currentWeek || !fetchLocksForWeek) return;

      try {
        // Fetch locks for current week and a few future weeks to ensure we have the data
        const weeksToFetch = [];
        for (let week = Math.max(1, currentWeek - 1); week <= currentWeek + 5; week++) {
          if (!locksCache[week]) {
            weeksToFetch.push(week);
          }
        }

        // Fetch locks for each week that's not in cache
        for (const week of weeksToFetch) {
          await fetchLocksForWeek(week);
        }
      } catch (err) {
        console.error('Error fetching locks for League Stats:', err);
      }
    };

    fetchAllLocks();
  }, [currentWeek, fetchLocksForWeek, locksCache]);

  const fetchLeaderboardData = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await api.getLeaderboard(selectedSeason || effectiveSeason, settings);
      setLeaderboardData(response.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [selectedSeason, effectiveSeason, settings]);

  useEffect(() => {
    fetchLeaderboardData(false);
  }, [fetchLeaderboardData]);

  // Refresh overview grid when live scores update
  useEffect(() => {
    const socket = getSocket();
    if (!socket || activeView !== 'overview') return undefined;

    let refreshTimer;
    const handleLiveUpdates = () => {
      if ((selectedSeason || effectiveSeason) !== effectiveSeason) return;

      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        fetchLeaderboardData(true);
      }, 500);
    };

    socket.on('live_game_updates', handleLiveUpdates);
    return () => {
      clearTimeout(refreshTimer);
      socket.off('live_game_updates', handleLiveUpdates);
    };
  }, [activeView, selectedSeason, effectiveSeason, fetchLeaderboardData]);

  // Function to merge leaderboard data with all active users for League Overview - use same ranking logic as leaderboard
  const getUsersForOverview = () => {
    if (activeView !== 'overview') {
      return leaderboardData?.users || leaderboardData;
    }

    const leaderboardUsers = leaderboardData?.users || leaderboardData;
    const weekStructure = leaderboardData?.weekStructure;

    if (!leaderboardUsers || !Array.isArray(leaderboardUsers) || !allActiveUsers.length) {
      return leaderboardUsers;
    }

    // Create a map of existing leaderboard users
    const existingUsersMap = new Map();
    leaderboardUsers.forEach(user => {
      existingUsersMap.set(user.username, user);
    });

    // Add missing active users to the end of the leaderboard data
    const missingUsers = allActiveUsers.filter(username => !existingUsersMap.has(username));

    const missingUserData = missingUsers.map(username => {
      const byeWeekRequired = settings?.bye_week_required;
      const locksRemaining = byeWeekRequired ? '20+Bye' : '21';

      return {
        username: username,
        rank: 'TBD',
        wins: 0,
        losses: 0,
        bye_used: 0,
        effective_losses: 0,
        accumulated_margin: 0,
        average_margin: 0,
        locks_remaining: locksRemaining,
        best_possible: 20,
        fav_pics: 0,
        dog_picks: 0,
        pk_picks: 0,
        over_picks: 0,
        under_picks: 0,
        weeks: weekStructure ? weekStructure.map(week => ({
          weekNumber: week.weekNumber,
          status: 'none'
        })) : []
      };
    });

    // Return leaderboard users in their original order, followed by missing users
    return [...leaderboardUsers, ...missingUserData];
  };

  // Function to get users for leaderboard view - maintain backend ranking but include all active users
  const getUsersForLeaderboard = () => {
    if (activeView !== 'leaderboard') {
      return leaderboardData?.users || leaderboardData;
    }

    const leaderboardUsers = leaderboardData?.users || leaderboardData;
    const weekStructure = leaderboardData?.weekStructure;

    if (!leaderboardUsers || !Array.isArray(leaderboardUsers) || !allActiveUsers.length) {
      return leaderboardUsers;
    }

    // Create a map of existing leaderboard users
    const existingUsersMap = new Map();
    leaderboardUsers.forEach(user => {
      existingUsersMap.set(user.username, user);
    });

    // Add missing active users to the end of the leaderboard data
    const missingUsers = allActiveUsers.filter(username => !existingUsersMap.has(username));

    const missingUserData = missingUsers.map(username => {
      const byeWeekRequired = settings?.bye_week_required;
      const locksRemaining = byeWeekRequired ? '20+Bye' : '21';

      return {
        username: username,
        rank: 'TBD',
        wins: 0,
        losses: 0,
        bye_used: 0,
        effective_losses: 0,
        accumulated_margin: 0,
        average_margin: 0,
        locks_remaining: locksRemaining,
        best_possible: 20,
        fav_pics: 0,
        dog_picks: 0,
        pk_picks: 0,
        over_picks: 0,
        under_picks: 0,
        weeks: weekStructure ? weekStructure.map(week => ({
          weekNumber: week.weekNumber,
          status: 'none'
        })) : []
      };
    });

    // Return leaderboard users in their original order, followed by missing users
    return [...leaderboardUsers, ...missingUserData];
  };

  // Function to get weeks data for a user from the new API structure
  const getUserWeeks = (user) => {
    const weekStructure = leaderboardData?.weekStructure;
    if (!weekStructure || !Array.isArray(weekStructure)) {
      return [];
    }

    // If user already has weeks data (from old API format), use it
    if (user.weeks && Array.isArray(user.weeks)) {
      return user.weeks;
    }

    // For new API format, construct weeks from weeklyPicks and weekStructure
    const weeklyPicks = user.weeklyPicks || {};

    return weekStructure.map(week => {
      // The weeklyPicks structure is: weeklyPicks[weekNumber][pickNumber] = status
      const weekPicks = weeklyPicks[week.weekNumber] || {};
      const pickStatus = weekPicks[week.pickNumber] || 'none';

      return {
        weekNumber: week.weekNumber,
        status: pickStatus
      };
    });
  };

  const renderTable = () => {
    // Handle season not started case
    if (leaderboardData && !leaderboardData.seasonStarted) {
      return (
        <div className="season-not-started">
          <h3>{leaderboardData.message}</h3>
        </div>
      );
    }

    // Handle the new response format
    const users = activeView === 'leaderboard' ? getUsersForLeaderboard() : getUsersForOverview();
    const weekStructure = leaderboardData?.weekStructure;

    console.log('Users after sorting:', users.map(u => ({ username: u.username, rank: u.rank })));
    console.log('First 5 users in display order:', users.slice(0, 5).map(u => ({ username: u.username, rank: u.rank })));

    if (!users || !Array.isArray(users)) {
      console.log('No leaderboard data available:', leaderboardData);
      return null;
    }

    // Check if we should show stale data message
    // State 2: Hide Locks / Show Lockers - show stale data message for leaderboard only (not overview since we show "PM")
    // State 4: Hide Locks / Hide Lockers - show stale data message for both views
    // State 3: Hide Locker / Show Lock - show stale data message only for leaderboard, not overview
    const showStaleDataMessage = (activeView === 'leaderboard' && !hasReachedMaxPicks() && !settings.show_lockers && selectedSeason === effectiveSeason) ||
                                (activeView === 'overview' && !hasReachedMaxPicks() && !settings.show_locks && !settings.show_lockers && selectedSeason === effectiveSeason);
    const previousWeek = currentWeek > 1 ? currentWeek - 1 : 1;




    return (
      <>
        {showStaleDataMessage && (
          <div className="stale-data-message">
            <p>
              {activeView === 'leaderboard'
                ? currentWeek === 1
                  ? "Week 1 leaderboard will be available once you complete your lock."
                  : `Leaderboard reflects the ranking as of the end of Week ${previousWeek}. Live leaderboard will be visible after you complete your lock.`
                : currentWeek === 1
                  ? "Week 1 data will be available once you complete your lock."
                  : `League overview reflects data through Week ${previousWeek}. Full data will be available after you complete your lock.`
              }
            </p>
          </div>
        )}
        <table className={`leaderboard-table ${activeView}`}>
        <thead>
          <tr>
            {/* Static columns - always visible */}
            <th className="static-column">
              RANK
              {!settings?.bye_week_required && (
                <span className="info-icon" title="Rankings account for unused bye weeks - users with unused byes get credit for potential loss replacement">ⓘ</span>
              )}
            </th>
            <th className="static-column">NAME</th>
            <th className="static-column">WINS</th>
            <th className="static-column">LOSSES</th>
            <th className="static-column">BYE USED</th>

            {/* Leaderboard-specific columns - mark as scrollable */}
            <th className="scrollable-column leaderboard-column">WIN %</th>
            <th className="scrollable-column leaderboard-column">GB LEADER</th>
            <th className="scrollable-column leaderboard-column">TOTAL MARGIN</th>
            <th className="scrollable-column leaderboard-column">AVG MARGIN</th>
            <th className="scrollable-column info-column locks-column">
              Locks Left
              {' '}
              <span className="info-icon" title="Number of locks still needed to be made">ⓘ</span>
            </th>
            <th className="scrollable-column leaderboard-column">BEST POSSIBLE</th>
            <th className="scrollable-column leaderboard-column">FAV</th>
            <th className="scrollable-column leaderboard-column">DOG</th>
            <th className="scrollable-column leaderboard-column">PK</th>
            <th className="scrollable-column leaderboard-column">OVER</th>
            <th className="scrollable-column leaderboard-column">UNDER</th>

            {/* Overview columns - always render */}
            {weekStructure?.map((week) => (
              <th
                key={`header-week-${week.weekNumber}-${week.pickNumber}-${week.isBowlWeek ? 'bowl' : 'regular'}`}
                className="scrollable-column overview-column"
              >
                {week.isBowlWeek ? getWeekDisplay(week) : `W${week.weekNumber}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((player, index) => {
            // Calculate win percentage
            const totalGames = (player.wins || 0) + (player.losses || 0);
            const winPct = totalGames > 0 ? (player.wins || 0) / totalGames : 0;

            return (
              <tr key={`${player.username}-${player.rank}`}>
                {/* Static columns */}
                <td className="static-column">{player.rank || '-'}</td>
                <td className="static-column">{player.username}</td>
                <td className="static-column">{player.wins || 0}</td>
                <td className="static-column">{player.losses || 0}</td>
                <td className="static-column">{player.bye_used === 1 ? 'Yes' : 'No'}</td>

                {/* Leaderboard columns */}
                <td className="scrollable-column leaderboard-column">
                  {(winPct * 100).toFixed(1)}%
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getGamesBehindDisplay(player?.gamesBehind)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getMarginDisplay(player?.accumulated_margin)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getMarginDisplay(player?.average_margin)}
                </td>
                <td className="scrollable-column info-column locks-column">
                  {getLocksRemainingDisplay(player.locks_remaining, player.bye_used)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {getBestPossibleDisplay(player.best_possible, player.losses, player.effective_losses)}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.fav_pics || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.dog_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.pk_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.over_picks || 0}
                </td>
                <td className="scrollable-column leaderboard-column">
                  {player.under_picks || 0}
                </td>

                {/* Overview columns */}
                {activeView === 'overview' && getUserWeeks(player).map((week, weekIndex) => {
                  // Get the week number from the weekStructure array
                  const weekStructureWeek = weekStructure?.[weekIndex];
                  const weekNumber = weekStructureWeek?.weekNumber || (weekIndex + 1);
                  const weekKey = `${player.username}-week-${weekIndex}-${weekNumber}-${weekStructureWeek?.pickNumber || 'undefined'}-${weekStructureWeek?.isBowlWeek ? 'bowl' : 'regular'}`;

                  // Check if this is the current week and should be blurred (States 3 & 4)
                  const isCurrentWeek = weekNumber === currentWeek;
                  const isFutureWeek = weekNumber > currentWeek;

                  // Check if any other user has made a lock for this future week
                  const hasOtherUserLockedFutureWeek = isFutureWeek && locksCache[weekNumber] &&
                    locksCache[weekNumber].some(lock =>
                      lock.userId !== user?.id && lock.status === 'locked'
                    );

                  // Check if this specific user has made a lock for this week
                  // Include 'locked', 'completed', and 'in_progress' statuses to handle completed locks
                  const hasThisUserLockedWeek = locksCache[weekNumber] &&
                    locksCache[weekNumber].some(lock =>
                      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
                    );

                                    // Determine if this week should be blurred
                  // State 1 (Show Everything): show_locks=true, show_lockers=true - show all statuses
                  // State 2 (Hide Locks): show_locks=false, show_lockers=true - show only PM for pending
                  // State 3 (Hide Lockers): show_locks=true, show_lockers=false - blur until user makes pick
                  // State 4 (Hide Everything): show_locks=false, show_lockers=false - blur until user makes pick

                  // Only blur in limited visibility states (3 & 4) when user hasn't made a pick
                  // In State 1 (show_locks=true, show_lockers=true), always show statuses
                  // In State 2 (show_locks=false, show_lockers=true), show only PM for pending
                  // In States 3 & 4 (show_lockers=false), blur until user makes a pick
                  const shouldBlurCurrentWeek = !hasReachedMaxPicks() && !settings.show_lockers && selectedSeason === effectiveSeason && isCurrentWeek;
                  const shouldBlurFutureWeek = isFutureWeek && hasOtherUserLockedFutureWeek && !hasThisUserLockedWeek;
                  const shouldBlur = shouldBlurCurrentWeek || shouldBlurFutureWeek;

                  // In State 2 (Hide Locks / Show Lockers), only show "PM" for pending games, not outcomes
                  const shouldShowOnlyPM = !hasReachedMaxPicks() && !settings.show_locks && settings.show_lockers && selectedSeason === effectiveSeason && isCurrentWeek;
                  const statusLabel = getWeekStatus(week.status);
                  const liveWeekCell = isLiveWeekStatus(week.status);

                  return (
                    <td
                      key={weekKey}
                      className={`overview-column week-cell ${week.status || 'none'}${liveWeekCell ? ' live-status-pulse' : ''}`}
                    >
                      {shouldBlur ? (
                        <HiddenText
                          text="NT"
                          tooltip={shouldBlurFutureWeek
                            ? "Nice try! Future week lock hidden until you make your own lock for this week."
                            : "Nice try! Lock status hidden until you make your pick."
                          }
                          showTooltip={true}
                        />
                      ) : shouldShowOnlyPM ? (
                        // In State 2, only show "PM" for pending games, hide outcomes
                        week.status === 'pending' || week.status === 'pm' ? 'PM' : '-'
                      ) : (
                        <LivePulseValue value={statusLabel}>
                          {statusLabel}
                        </LivePulseValue>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </>
    );
  };

  if (isMobile) {
    return (
      <MobileLeagueStatsShell
        selectedSeason={selectedSeason}
        effectiveSeason={effectiveSeason}
        availableSeasons={availableSeasons}
        onSeasonChange={setSelectedSeason}
        leaderboardData={leaderboardData}
        loading={loading}
        error={error}
        onRefreshLeaderboard={() => fetchLeaderboardData(true)}
      />
    );
  }

  if (loading) {
    return (
      <div className="leaderboard-page">
        {renderViewToggle()}
        <LeaderboardTableSkeleton rowCount={12} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-page">
        {renderViewToggle()}
        <div className="error-container">{error}</div>
      </div>
    );
  }

  const isArchivedSeason = selectedSeason && selectedSeason < effectiveSeason;

  return (
    <div className={`leaderboard-page ${isArchivedSeason ? 'archived-season' : ''}`}>
      {renderViewToggle()}
             {activeView === 'metrics' ? (
         <div className="metrics-container">
           <LeagueMetrics
             leaderboardData={leaderboardData}
             season={selectedSeason || effectiveSeason}
           />
         </div>
             ) : activeView === 'lock-overview' ? (
         <div className={`lock-overview-container ${isArchivedSeason ? 'archived-season' : ''}`}>
           <LockOverview
             season={selectedSeason || effectiveSeason}
             leaderboardData={leaderboardData}
           />
         </div>
      ) : (
        <div className="leaderboard-table-wrapper">
          <div className="leaderboard-container">
            {renderTable()}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueStats;
